import { TFunction } from 'next-i18next';
import { isDecimalNumber } from '../validateNumber';
import { getDiscipline } from './discipline';
import { DisciplineEnum } from 'types/types';
import {
    FieldTypeApiEnum,
    GoalInterface,
    PPSettingsContumacyTypeEnum,
    PunishmentInterface,
    PunishmentTypeEnum,
    RotationInterface,
    ScoreApiInterface,
} from 'submodules/api_middleware';
import {
    EditFieldInterface,
    FilterSize,
    LabelSize,
    StateInterface,
    ValidateValueType,
    ValueOnChangeRemoveType,
} from 'components/interfaces/GeneralInterface';
import {
    MATCH_DURATION_FOOTBALL,
    MATCH_DURATION_FUTSAL,
    MATCH_MAX_PLAYERS_FOOTBALL,
    MATCH_MAX_PLAYERS_FUTSAL,
} from 'constants/general';

export const stringifyScore = (score: ScoreApiInterface): string =>
    score
        ? `${
              score.home_team || score.home_team === 0 ? score.home_team : '-'
          }:${
              score.guest_team || score.guest_team === 0
                  ? score.guest_team
                  : '-'
          }`
        : null;

export const getState = (
    t: TFunction,
    recordState: number,
    matchState: number,
    contumacy_type: PPSettingsContumacyTypeEnum
): string => {
    switch (matchState) {
        case 4:
            return `${t('MemberProfile.contumacy')} - ${t(
                `ProceedingPunishments.values.${contumacy_type}`
            )}`;
        case 5:
            return t('MemberProfile.postponed');
        case 6:
            return t('match.not_started');
        default: {
            switch (recordState) {
                case 1:
                    return t('Match.states.locked');
                case 2:
                    return t('Match.states.fillState');
                case 3:
                    return t('Match.states.checkBeforeMatch');
                case 4:
                    return t('Match.states.launched');
                case 5:
                    return t('Match.states.regConfirm');
                case 6:
                    return t('Match.states.closed');
            }
        }
    }
};

export const getScheduleStateText = (t: TFunction, key: string): string =>
    t(`Match.scheduleStates.${key}`);

export const isValueABC = (value: string): boolean =>
    /^([a-c]|[A-C]){1}$/.test(value);
export const isValueLowerCaseABC = (value: string): boolean =>
    /^[a-c]$/.test(value);

const getRegularMinuteFromAddedTimeFormat = (minute: string) => {
    return minute.split('+')[0];
};

const getAddedMinuteFromAddedTimeFormat = (minute: string) => {
    return minute.split('+')[1];
};

const getRegularMinuteFromAddedTimeShortFormat = (
    minute: string,
    fullTime: number
) => {
    const m = minute.replace('+', '');
    const isFirstHalf = Number(m) - fullTime < 0;

    if (isFirstHalf) {
        const halfTime = fullTime / 2;
        return Number(m) >= halfTime ? halfTime : Number(m);
    }

    return fullTime;
};

const getAddedMinuteFromAddedTimeShortFormat = (
    minute: string,
    fullTime: number
) => {
    const m = minute.replace('+', '');
    const isFirstHalf = Number(m) - fullTime < 0;

    if (isFirstHalf) {
        const addedMinute = Number(m) - fullTime / 2;
        return addedMinute > 0 ? addedMinute : 0;
    }

    return Number(m) - fullTime;
};

export const isMinuteAGreaterThanMinuteB = (
    minuteA: string,
    minuteB: string,
    matchDurationMinutes?: number
): boolean => {
    const fullTime =
        matchDurationMinutes || getDiscipline() === DisciplineEnum.football
            ? MATCH_DURATION_FOOTBALL
            : MATCH_DURATION_FUTSAL;
    const halfTime = matchDurationMinutes / 2;

    const isInRegularTime = (v: string) => !isNaN(Number(v));
    const isInAddedTime = (v: string) =>
        v.includes('+') && getAddedMinuteFromAddedTimeFormat(v) !== '';
    const isInAddedTimeShortFormat = (v: string) =>
        v.includes('+') && getAddedMinuteFromAddedTimeFormat(v) === '';
    const isBeforeMatchStarted = (v: string) => v === 'A';
    const isInHalfTimeBreak = (v: string) => v === 'B';
    const isAfterMatchEnded = (v: string) => v === 'C';

    // A > B conditions
    const isBeforeMatchStarted_condition =
        !isBeforeMatchStarted(minuteA) && isBeforeMatchStarted(minuteB);

    const isAfterMatchEnded_condition =
        isAfterMatchEnded(minuteA) && !isAfterMatchEnded(minuteB);

    const isInHalfTimeBreak_regularTime_condition =
        isInHalfTimeBreak(minuteA) &&
        isInRegularTime(minuteB) &&
        halfTime > Number(minuteB);

    const isInHalfTimeBreak_addedTime_condition =
        isInHalfTimeBreak(minuteA) &&
        isInAddedTime(minuteB) &&
        halfTime >= Number(getRegularMinuteFromAddedTimeFormat(minuteB));

    const isInHalfTimeBreak_addedTimeShortFormat_condition =
        isInHalfTimeBreak(minuteA) &&
        isInAddedTimeShortFormat(minuteB) &&
        halfTime >= getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime);

    const regularTimes_condition =
        isInRegularTime(minuteA) &&
        isInRegularTime(minuteB) &&
        Number(minuteA) > Number(minuteB);

    const regularTime_addedTime_condition =
        isInRegularTime(minuteA) &&
        isInAddedTime(minuteB) &&
        Number(minuteA) > Number(getRegularMinuteFromAddedTimeFormat(minuteB));

    const regularTime_addedTimeShortFormat_condition =
        isInRegularTime(minuteA) &&
        isInAddedTimeShortFormat(minuteB) &&
        Number(minuteA) >
            Number(getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime));

    const regularTime_inHalfTimeBreak_condition =
        isInRegularTime(minuteA) &&
        isInHalfTimeBreak(minuteB) &&
        Number(minuteA) > halfTime;

    const addedTime_regularTime_condition =
        isInAddedTime(minuteA) &&
        isInRegularTime(minuteB) &&
        Number(getRegularMinuteFromAddedTimeFormat(minuteA)) >= Number(minuteB);

    const addedTimes_condition =
        isInAddedTime(minuteA) &&
        isInAddedTime(minuteB) &&
        (Number(getRegularMinuteFromAddedTimeFormat(minuteA)) >
            Number(getRegularMinuteFromAddedTimeFormat(minuteB)) ||
            (Number(getRegularMinuteFromAddedTimeFormat(minuteA)) ===
                Number(getRegularMinuteFromAddedTimeFormat(minuteB)) &&
                Number(getAddedMinuteFromAddedTimeFormat(minuteA)) >
                    Number(getAddedMinuteFromAddedTimeFormat(minuteB))));

    const addedTime_addedTimeShortFormat_condition =
        isInAddedTime(minuteA) &&
        isInAddedTimeShortFormat(minuteB) &&
        (Number(getRegularMinuteFromAddedTimeFormat(minuteA)) >
            getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime) ||
            (Number(getRegularMinuteFromAddedTimeFormat(minuteA)) ===
                getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime) &&
                Number(getAddedMinuteFromAddedTimeFormat(minuteA)) >
                    getAddedMinuteFromAddedTimeShortFormat(minuteB, fullTime)));

    const addedTime_inHalfTimeBreak_condition =
        isInAddedTime(minuteA) &&
        isInHalfTimeBreak(minuteB) &&
        Number(getRegularMinuteFromAddedTimeFormat(minuteA)) > halfTime;

    const addedTimeShortFormat_regularTime_condition =
        isInAddedTimeShortFormat(minuteA) &&
        isInRegularTime(minuteB) &&
        getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) >=
            Number(minuteB);

    const addedTimeShortFormat_addedTime_condition =
        isInAddedTimeShortFormat(minuteA) &&
        isInAddedTime(minuteB) &&
        (getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) >
            Number(getRegularMinuteFromAddedTimeFormat(minuteB)) ||
            (getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) ===
                Number(getRegularMinuteFromAddedTimeFormat(minuteB)) &&
                getAddedMinuteFromAddedTimeShortFormat(minuteA, fullTime) >
                    Number(getAddedMinuteFromAddedTimeFormat(minuteB))));

    const addedTimeShortFormats_condition =
        isInAddedTimeShortFormat(minuteA) &&
        isInAddedTimeShortFormat(minuteB) &&
        (getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) >
            getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime) ||
            (getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) ===
                getRegularMinuteFromAddedTimeShortFormat(minuteB, fullTime) &&
                getAddedMinuteFromAddedTimeShortFormat(minuteA, fullTime) >
                    getAddedMinuteFromAddedTimeShortFormat(minuteB, fullTime)));

    const addedTimeShortFormat_inHalfTimeBreak_condition =
        isInAddedTimeShortFormat(minuteA) &&
        isInHalfTimeBreak(minuteB) &&
        getRegularMinuteFromAddedTimeShortFormat(minuteA, fullTime) > halfTime;

    if (
        isBeforeMatchStarted_condition ||
        isAfterMatchEnded_condition ||
        isInHalfTimeBreak_regularTime_condition ||
        isInHalfTimeBreak_addedTime_condition ||
        isInHalfTimeBreak_addedTimeShortFormat_condition ||
        regularTimes_condition ||
        regularTime_addedTime_condition ||
        regularTime_addedTimeShortFormat_condition ||
        regularTime_inHalfTimeBreak_condition ||
        addedTime_regularTime_condition ||
        addedTimes_condition ||
        addedTime_addedTimeShortFormat_condition ||
        addedTime_inHalfTimeBreak_condition ||
        addedTimeShortFormat_regularTime_condition ||
        addedTimeShortFormat_addedTime_condition ||
        addedTimeShortFormats_condition ||
        addedTimeShortFormat_inHalfTimeBreak_condition
    ) {
        return true;
    }

    return false;
};

export const validateMinuteField = (
    t: TFunction,
    value: ValidateValueType,
    isExtended: boolean,
    redCardMinute?: string,
    redCardMinutePlayer2?: string,
    matchDurationMinutes?: number,
    cardType?: PunishmentTypeEnum,
    goals?: GoalInterface[],
    rotations?: RotationInterface[],
    yellowPunishments?: PunishmentInterface[]
): string => {
    if (!isValidMinute(value as string, isExtended)) {
        return t('match.minuteFormatInvalid');
    }

    if (cardType === PunishmentTypeEnum.RED) {
        const YCMinutes = yellowPunishments
            ? yellowPunishments.map((p) => p.match_minute)
            : [];
        const GMinutes = goals ? goals.map((g) => g.match_minute) : [];
        const RMinutes = rotations ? rotations.map((r) => r.match_minute) : [];
        const minutes = [...YCMinutes, ...GMinutes, ...RMinutes];
        for (let i = 0; i < minutes.length; i++) {
            if (
                isMinuteAGreaterThanMinuteB(
                    minutes[i],
                    value as string,
                    matchDurationMinutes
                )
            ) {
                return t('Match.red_card_minute_error');
            }
        }
    } else {
        if (
            redCardMinute &&
            isMinuteAGreaterThanMinuteB(
                value as string,
                redCardMinute,
                matchDurationMinutes
            )
        ) {
            return t('Player.hasRedCardPunishment', { minute: redCardMinute });
        }
        if (
            redCardMinutePlayer2 &&
            isMinuteAGreaterThanMinuteB(
                value as string,
                redCardMinutePlayer2,
                matchDurationMinutes
            )
        ) {
            return t('Player.hasRedCardPunishment', {
                minute: redCardMinutePlayer2,
            });
        }
    }

    return undefined;
};

export const validateMatchDurationField = (
    t: TFunction,
    value: ValidateValueType
): string => {
    if (!isValidMatchDuration(value as string)) {
        return t('match.minuteFormatInvalid');
    }

    return undefined;
};

export const isValidMinute = (value: string, isExtended: boolean): boolean => {
    if (!value) {
        return true;
    }

    if (isExtended && isValueABC(value)) {
        return true;
    }

    const val = value.split('+');
    if (
        (value.startsWith('0') && value.length > 1) ||
        value.includes('.') ||
        value.includes(',') ||
        value.includes('-') ||
        val.some((v) => v.length > 3) ||
        val.length > 2 ||
        val.some(
            (v) =>
                v &&
                ((v.startsWith('0') && v.length > 1) ||
                    !(isDecimalNumber(v) && Number(v) >= 0))
        )
    ) {
        return false;
    }

    return true;
};

interface GetMatchMinuteFieldProps {
    t: TFunction;
    minute: string;
    innerName?: string;
    innerLabel?: string;
    isExtended?: boolean;
    redCardMinute?: string;
    redCardMinutePlayer2?: string;
    matchDurationMinutes?: number;
    cardType?: PunishmentTypeEnum;
    goals?: GoalInterface[];
    rotations?: RotationInterface[];
    yellowPunishments?: PunishmentInterface[];
    requiredIfSuccess?: (state: StateInterface) => boolean;
    onChange?: (
        value: ValueOnChangeRemoveType,
        state: StateInterface
    ) => StateInterface;
}

export const getMatchMinuteField = ({
    t,
    minute,
    innerName,
    innerLabel,
    isExtended,
    redCardMinute,
    redCardMinutePlayer2,
    matchDurationMinutes,
    cardType,
    goals,
    rotations,
    yellowPunishments,
    requiredIfSuccess,
    onChange,
}: GetMatchMinuteFieldProps): EditFieldInterface => {
    const name = innerName ? innerName : 'minute';
    const label = innerLabel ? innerLabel : 'general.minute';
    return {
        name: name,
        value: minute,
        label: label,
        type: FieldTypeApiEnum.TEXT,
        size: FilterSize.THIRD,
        labelSize: LabelSize.TOP,
        required: requiredIfSuccess ? undefined : true,
        requiredIfSuccess: requiredIfSuccess,
        autoFocus: true,
        validate: (value: ValidateValueType) =>
            validateMinuteField(
                t,
                value,
                isExtended,
                redCardMinute,
                redCardMinutePlayer2,
                matchDurationMinutes,
                cardType,
                goals,
                rotations,
                yellowPunishments
            ),
        onChange: (value: ValueOnChangeRemoveType, state: StateInterface) => {
            const newState = onChange ? onChange(value, state) : state;
            const val = value as string;
            return {
                ...newState,
                [name]: isValueLowerCaseABC(val) ? val.toUpperCase() : val,
            };
        },
        tooltip: t(
            `Player.${isExtended ? 'minuteTooltipExtended' : 'minuteTooltip'}`
        ),
    };
};

export const isValidMatchDuration = (value: string): boolean => {
    if (!value) {
        return true;
    }

    const val = value.split('+');
    if (
        val.length > 3 ||
        val.some((v) => !(isDecimalNumber(v) && Number(v) >= 0))
    ) {
        return false;
    }

    return true;
};

export const getMaxPlayers = (): number =>
    getDiscipline() === DisciplineEnum.football
        ? MATCH_MAX_PLAYERS_FOOTBALL
        : MATCH_MAX_PLAYERS_FUTSAL;

export const getRefereeShortcut = (key: string): string => {
    if (key === 'main') {
        return 'R';
    }

    return key.toUpperCase();
};
